import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getProductionPlanner, fmt } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { LineChart, RefreshCw, AlertTriangle, PackageCheck, CheckCircle2, Factory, Info, CalendarPlus } from "lucide-react";
import { PlanFromRecommendationsDialog } from "@/pages/planning/PlanFromRecommendationsDialog";

const StatCard = ({ icon: Icon, label, value, tone = "brand", testid }) => {
  const toneCls = {
    brand: "bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]",
    danger: "bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]",
    muted: "bg-secondary text-muted-foreground",
  }[tone];
  return (
    <Card data-testid={testid}>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${toneCls}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="truncate text-xs text-muted-foreground">{label}</div>
          <div className="font-mono text-2xl font-semibold leading-tight tabular-nums">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const Num = ({ value, unit, decimals = 1, className = "" }) => (
  <span className={`font-mono tabular-nums ${className}`}>
    {value == null ? "—" : fmt(value, decimals)}{unit ? <span className="ml-0.5 text-xs text-muted-foreground">{unit}</span> : null}
  </span>
);

export function PlannerRecommendations({ onPlanned }) {
  const { t, td } = useT();
  const [addDialog, setAddDialog] = useState(null); // null | { onlyProduct?: string }
  const [horizon, setHorizon] = useState(14);
  const [lookback, setLookback] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getProductionPlanner({ horizon_days: horizon, lookback_days: lookback }));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [horizon, lookback]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.products || [];
  const summary = data?.summary || {};

  return (
    <div data-testid="planner-recommendations">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]">
            <LineChart className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">{t("planner.title")}</h2>
            <p className="max-w-2xl text-sm text-muted-foreground">{t("planner.subtitle")}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="planner-refresh">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> {t("planner.refresh")}
          </Button>
          <Button size="sm" onClick={() => setAddDialog({})} disabled={loading || !(summary.to_produce_count > 0)} data-testid="planner-add-all">
            <CalendarPlus className="mr-1.5 h-4 w-4" /> {t("planner.addToCalendar", { n: summary.to_produce_count || 0 })}
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card className="mb-5">
        <CardContent className="flex flex-wrap items-end gap-x-8 gap-y-4 p-4">
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">{t("planner.horizon")}</div>
            <ToggleGroup
              type="single" value={String(horizon)}
              onValueChange={(v) => v && setHorizon(Number(v))}
              className="justify-start" data-testid="planner-horizon"
            >
              <ToggleGroupItem value="7" data-testid="planner-horizon-7">{t("planner.days7")}</ToggleGroupItem>
              <ToggleGroupItem value="14" data-testid="planner-horizon-14">{t("planner.days14")}</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">{t("planner.lookback")}</div>
            <Select value={String(lookback)} onValueChange={(v) => setLookback(Number(v))}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="planner-lookback"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[7, 14, 30, 60, 90].map((d) => (
                  <SelectItem key={d} value={String(d)} data-testid={`planner-lookback-${d}`}>{t("planner.lastNDays", { n: d })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="max-w-md text-xs text-muted-foreground">{t("planner.methodHint", { horizon, lookback })}</p>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard icon={Factory} label={t("planner.toProduceCount")} value={loading ? "…" : (summary.to_produce_count ?? 0)} tone="brand" testid="planner-summary-count" />
        <StatCard icon={PackageCheck} label={t("planner.totalToProduce")} value={loading ? "…" : `${fmt(summary.total_recommend_kg ?? 0)} kg`} tone="brand" testid="planner-summary-produce" />
        <StatCard icon={AlertTriangle} label={t("planner.totalAtRisk")} value={loading ? "…" : `${fmt(summary.total_at_risk_kg ?? 0)} kg`} tone="danger" testid="planner-summary-risk" />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center" data-testid="planner-empty">
              <PackageCheck className="h-10 w-10 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">{t("planner.empty")}</p>
            </div>
          ) : (
            <TooltipProvider delayDuration={150}>
              <div className="overflow-x-auto">
                <Table data-testid="planner-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("planner.colProduct")}</TableHead>
                      <TableHead className="text-right">
                        <ColHead label={t("planner.colSold", { n: lookback })} hint={t("planner.colSoldHint")} />
                      </TableHead>
                      <TableHead className="text-right">
                        <ColHead label={t("planner.colVelocity")} hint={t("planner.colVelocityHint")} />
                      </TableHead>
                      <TableHead className="text-right">
                        <ColHead label={t("planner.colDemand", { n: horizon })} hint={t("planner.colDemandHint")} />
                      </TableHead>
                      <TableHead className="text-right">{t("planner.colInStock")}</TableHead>
                      <TableHead className="text-right">
                        <ColHead label={t("planner.colUsable")} hint={t("planner.colUsableHint")} />
                      </TableHead>
                      <TableHead className="text-right">
                        <ColHead label={t("planner.colAtRisk")} hint={t("planner.colAtRiskHint")} />
                      </TableHead>
                      <TableHead className="text-right">
                        <ColHead label={t("planner.colCover")} hint={t("planner.colCoverHint")} />
                      </TableHead>
                      <TableHead className="text-right">{t("planner.colRecommend")}</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => {
                      const needs = r.recommend_kg > 0;
                      return (
                        <TableRow key={r.product_type}
                          className={needs ? "bg-[hsl(var(--brand)/0.04)]" : ""}
                          data-testid={`planner-row-${r.product_type}`}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {needs && <span className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--brand))]" title={t("planner.needsProduction")} />}
                              <span>{td(r.product_type)}</span>
                            </div>
                            {r.last_sold_date && (
                              <div className="text-[11px] text-muted-foreground">{t("planner.lastSold", { date: r.last_sold_date })}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-right"><Num value={r.sold_lookback_kg} unit="kg" /></TableCell>
                          <TableCell className="text-right"><Num value={r.velocity_kg_day} unit="kg/d" decimals={2} /></TableCell>
                          <TableCell className="text-right font-medium"><Num value={r.demand_kg} unit="kg" /></TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            <Num value={r.on_hand_kg} unit="kg" />
                            {r.reserved_kg > 0 && (
                              <div className="text-[11px]">{t("planner.reservedShort", { kg: fmt(r.reserved_kg) })}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-right"><Num value={r.usable_kg} unit="kg" /></TableCell>
                          <TableCell className="text-right">
                            {r.at_risk_kg > 0 ? (
                              <Badge variant="secondary" className="border-[hsl(var(--danger)/0.3)] bg-[hsl(var(--danger)/0.12)] font-mono text-[hsl(var(--danger))]"
                                data-testid={`planner-risk-${r.product_type}`}>
                                {fmt(r.at_risk_kg)} kg
                              </Badge>
                            ) : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <Num value={r.days_of_cover} unit={r.days_of_cover != null ? "d" : ""} />
                          </TableCell>
                          <TableCell className="text-right">
                            {needs ? (
                              <div data-testid={`planner-recommend-${r.product_type}`}>
                                <span className="font-mono font-semibold text-[hsl(var(--brand))]">{fmt(r.recommend_kg)} kg</span>
                                {r.recommend_pkg_qty ? (
                                  <div className="text-[11px] text-muted-foreground">
                                    ≈ {r.recommend_pkg_qty} × {r.recommend_unit}
                                  </div>
                                ) : null}
                                {r.suggested_milk_l ? (
                                  <div className="text-[11px] text-muted-foreground" data-testid={`planner-milk-${r.product_type}`}>
                                    ≈ {fmt(r.suggested_milk_l, 0)} L {t("planner.milkShort")}
                                  </div>
                                ) : null}
                                {r.planned_count > 0 ? (
                                  <div className="text-[11px] text-[hsl(var(--info))]" data-testid={`planner-planned-${r.product_type}`}>
                                    {t("planner.addAlreadyPlanned", { l: fmt(r.planned_milk_l, 0), n: r.planned_count })}
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`planner-covered-${r.product_type}`}>
                                <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--brand))]" /> {t("planner.covered")}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="pl-0 text-right">
                            {needs && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setAddDialog({ onlyProduct: r.product_type })} data-testid={`planner-plan-${r.product_type}`} aria-label={t("planner.planThis")}>
                                    <CalendarPlus className="h-4 w-4 text-[hsl(var(--brand))]" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t("planner.planThis")}</TooltipContent>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      {data?.generated_at && !loading && (
        <p className="mt-3 text-right text-[11px] text-muted-foreground">
          {t("planner.generatedAt", { time: new Date(data.generated_at).toLocaleString() })}
        </p>
      )}

      <PlanFromRecommendationsDialog
        open={!!addDialog}
        rows={rows}
        onlyProduct={addDialog?.onlyProduct}
        horizon={horizon}
        onClose={() => setAddDialog(null)}
        onCreated={() => { load(); onPlanned?.(); }}
      />
    </div>
  );
}

const ColHead = ({ label, hint }) => (
  <span className="inline-flex items-center justify-end gap-1">
    {label}
    {hint && (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help text-muted-foreground/60"><Info className="h-3 w-3" /></span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[220px] text-xs">{hint}</TooltipContent>
      </Tooltip>
    )}
  </span>
);
